import React from 'react';
import { graphql } from 'gatsby';
import Layout from '../components/Layout';
import PageBanner from '../components/PageBanner';
import Section from '../components/Section';
import { Container, Row, Column } from '../components/Grid';
import Image from '../components/Image';
import MultiVideoPlayer from '../components/MultiVideoPlayer';
import OutboundLink from '../components/OutboundLink';
import Quicklinks from '../components/Quicklinks';
import Button from '../components/Button';
import Video from '../components/Video';
import VTSPlayer from '../components/VTSPlayer';
import AboutPartners from '../components/AboutPartners';
import './page.scss';

/**
 * Groups consecutive image sections together so they can be rendered
 * as a 3-column grid. Single isolated images stay as-is.
 */
const groupSections = (sections) => {
  if (!sections) return [];
  const result = [];
  let i = 0;
  while (i < sections.length) {
    if (sections[i].type === 'image') {
      const group = [];
      while (i < sections.length && sections[i].type === 'image') {
        group.push(sections[i]);
        i++;
      }
      if (group.length > 1) {
        result.push({ type: 'imageGroup', images: group });
      } else {
        result.push(group[0]);
      }
    } else {
      result.push(sections[i]);
      i++;
    }
  }
  return result;
};

const renderSection = (section, i) => {
  switch (section.type) {
    case 'heading': {
      const Tag = `h${section.level}`;
      return (
        <Row key={i}>
          <Column>
            <Tag>{section.text}</Tag>
          </Column>
        </Row>
      );
    }

    case 'paragraph':
      return (
        <Row key={i}>
          <Column>
            <p>
              {section.link ? (
                <a href={section.link} target="_blank" rel="noopener noreferrer">{section.text}</a>
              ) : section.text}
            </p>
          </Column>
        </Row>
      );

    case 'image':
      return (
        <Row key={i}>
          <Column size={6}>
            <Image
              filename={section.src.replace(/^.*\//, '')}
              alt={section.alt || ''}
            />
          </Column>
        </Row>
      );

    case 'imageGroup':
      return (
        <Row key={i} className="page-image-grid">
          {section.images.map((img, j) => (
            <Column key={j} size={4}>
              <Image
                filename={img.src.replace(/^.*\//, '')}
                alt={img.alt || ''}
              />
            </Column>
          ))}
        </Row>
      );

    case 'video':
      return (
        <Row key={i}>
          <Column>
            <Video
              src={section.src}
              title={section.title || 'Video'}
              className="page-video"
            />
          </Column>
        </Row>
      );

    case 'rtuCard': {
      const isFeature = !section.launchUrl && !!section.extraUrl;
      return (
        <div key={i} className={`rtu-card${isFeature ? ' rtu-card--feature' : ''}`}>
          {section.image && (
            <div className="rtu-card__image">
              {isFeature ? (
                <OutboundLink to={section.extraUrl} target="_blank" rel="noopener noreferrer">
                  <Image filename={section.image.replace(/^.*\//, '')} alt={section.alt || ''} />
                </OutboundLink>
              ) : (
                <Image filename={section.image.replace(/^.*\//, '')} alt={section.alt || ''} />
              )}
            </div>
          )}
          <div className="rtu-card__body">
            <h3 className="rtu-card__title">{section.title}</h3>
            {section.description && (
              <p className="rtu-card__description">{section.description}</p>
            )}
            {section.launchUrl && (
              <Button
                to={section.launchUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="rtu-card__btn"
                data-click-type="Module Launch"
                data-click-name={section.title || section.launchText || 'Ready to Use Activity'}
              >
                {section.launchText || 'Ready to Use Activity'}
              </Button>
            )}
            {section.extraUrl && (
              <OutboundLink
                to={section.extraUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="rtu-card__extra-link"
              >
                {section.extraText || 'Learn More'} →
              </OutboundLink>
            )}
          </div>
        </div>
      );
    }

    case 'videoChapters':
      return (
        <Row key={i}>
          <Column>
            <MultiVideoPlayer
              videos={section.chapters.map((ch) => ({
                src: ch.videoSrc,
                poster: ch.thumbnail.replace(/^.*\//, ''),
                title: ch.title,
              }))}
            />
          </Column>
        </Row>
      );

    case 'download':
      return (
        <Row key={i}>
          <Column>
            <Button
              to={section.href}
              target="_blank"
              rel="noopener noreferrer"
              className="page-download-btn"
            >
              {section.text || 'Download'}
            </Button>
          </Column>
        </Row>
      );

    case 'quicklinks':
      return (
        <Row key={i}>
          <Column>
            <Quicklinks
              title={section.title || null}
              links={(section.links || []).map((l) => ({
                target: l.path,
                label: l.label,
              }))}
              smoothScroll={false}
            />
          </Column>
        </Row>
      );

    case 'vtsPlayer':
      return (
        <Row key={i}>
          <Column>
            <VTSPlayer
              seriesTitle={section.seriesTitle}
              episodes={section.episodes || []}
              educatorGuide={section.educatorGuide || null}
            />
          </Column>
        </Row>
      );

    case 'twoColIntro':
      return (
        <Row key={i} className="page-two-col-intro">
          <Column size={4}>
            <h2 className="page-two-col-intro__heading">{section.title}</h2>
          </Column>
          <Column size={8}>
            <p>{section.text}</p>
          </Column>
        </Row>
      );

    case 'vftRtuCard': {
      return (
        <div key={i} className="vft-rtu-card">
          {section.image && (
            <div className="vft-rtu-card__image">
              <Image filename={section.image.replace(/^.*\//, '')} alt={section.alt || ''} />
            </div>
          )}
          <div className="vft-rtu-card__body">
            <h3 className="vft-rtu-card__title">{section.title}</h3>
            {section.grades && (
              <p className="vft-rtu-card__grades">{section.grades}</p>
            )}
            {section.description && (
              <p className="vft-rtu-card__description">{section.description}</p>
            )}
            {section.launchUrl && (
              <a
                href={section.launchUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="vft-rtu-card__launch-link"
                data-click-type="Module Launch"
                data-click-name={section.title || section.launchText || 'Open RTU'}
              >
                <svg className="vft-rtu-card__launch-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                  <path d="M8 12L3 7h3V1h4v6h3L8 12z" fill="currentColor"/>
                  <rect x="1" y="13" width="14" height="2" rx="1" fill="currentColor"/>
                </svg>
                {section.launchText || 'Open RTU'}
              </a>
            )}
          </div>
        </div>
      );
    }

    case 'resourceCards':
      return (
        <div key={i} className="resource-cards-grid">
          {(section.cards || []).map((card, j) => (
            <div key={j} className="resource-card">
              {card.image && (
                <div className="resource-card__image">
                  <Image
                    filename={card.image.replace(/^.*\//, '')}
                    alt={card.alt || ''}
                  />
                </div>
              )}
              <div className="resource-card__body">
                <h3 className="resource-card__title">{card.title}</h3>
                <p className="resource-card__description">{card.description}</p>
                {(card.downloads || []).length > 0 && (
                  <div className="resource-card__downloads">
                    {(card.downloads || []).map((dl, k) => (
                      <a
                        key={k}
                        href={dl.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="resource-card__download-link"
                      >
                        <svg className="resource-card__download-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                          <path d="M8 12L3 7h3V1h4v6h3L8 12z" fill="currentColor"/>
                          <rect x="1" y="13" width="14" height="2" rx="1" fill="currentColor"/>
                        </svg>
                        {dl.text}
                      </a>
                    ))}
                  </div>
                )}
                {card.activityUrl && (
                  <OutboundLink
                    to={card.activityUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="resource-card__activity-link"
                  >
                    <svg className="resource-card__activity-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                      <path d="M1 1h6v1.5H2.5v11h11V9H15v6a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1zm8 0h6v6h-1.5V3.56L7.28 9.78 6.22 8.72 12.44 2.5H9V1z" fill="currentColor"/>
                    </svg>
                    {card.activityText || 'Activity'}
                  </OutboundLink>
                )}
              </div>
            </div>
          ))}
        </div>
      );

    default:
      return null;
  }
};

/**
 * Generic page template — renders sections from a YAML data file.
 * Section types: heading | paragraph | image | imageGroup | videoChapters | download | quicklinks
 */
const Page = ({ data }) => {
  const page = data.pagesYaml;
  const isVft = page.slug && page.slug.startsWith('vft-');
  const isWillesden = page.slug === 'willesden-project';
  const grouped = groupSections(page.sections);

  // For Willesden, split into dark intro (before first h3) and main body
  let introSections = [];
  let bodySections = grouped;
  if (isWillesden) {
    const firstH3Idx = grouped.findIndex(
      (s) => s.type === 'heading' && s.level === 3
    );
    if (firstH3Idx > 0) {
      introSections = grouped.slice(0, firstH3Idx);
      bodySections = grouped.slice(firstH3Idx);
    }
  }

  return (
    <Layout title={page.title} description={page.metaDescription}>
      <PageBanner title={isVft ? 'Virtual Field Trip' : page.title} />
      {isVft && (
        <Section>
          <Container>
            <Row>
              <Column>
                <h2 className="page-vft-film-title">{page.title}</h2>
              </Column>
            </Row>
          </Container>
        </Section>
      )}

      {/* Willesden dark intro section */}
      {isWillesden && introSections.length > 0 && (
        <Section className="page-willesden-intro">
          <Container>
            {introSections.map((section, i) => renderSection(section, i))}
          </Container>
        </Section>
      )}

      {/* Main content section */}
      <Section className={isVft ? 'page-content-section' : undefined}>
        <Container>
          {bodySections.map((section, i) => renderSection(section, i))}
        </Container>
      </Section>

      <AboutPartners />
    </Layout>
  );
};

export default Page;

export const query = graphql`
  query PageTemplateQuery($id: String!) {
    pagesYaml(id: { eq: $id }) {
      slug
      path
      title
      metaDescription
      sections {
        type
        level
        text
        link
        src
        alt
        href
        title
        links {
          label
          path
        }
        image
        description
        launchUrl
        launchText
        extraUrl
        extraText
        grades
        chapters {
          title
          thumbnail
          videoSrc
        }
        cards {
          image
          alt
          title
          description
          downloads {
            href
            text
          }
          activityUrl
          activityText
        }
        seriesTitle
        episodes {
          title
          duration
          thumbnail
          shortDescription
          description
          src
          activityHref
        }
        educatorGuide {
          description
          href
        }
      }
    }
  }
`;
